import React, { ComponentType, useCallback, useContext, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { GuardedType, IsInterface, isString } from "generic-type-guard";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DataSelectors, Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { JSONDocument } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/models";
import { LocalizerHooks } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/services/localization";
import { isUaaOidcUser, UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LocalizedModelText } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { joinClassNames } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { DropDownItem } from "@com.mgmtp.a12.widgets/widgets-core/lib/dropdown";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import { Autocomplete, AutocompleteProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/autocomplete";
import { StyledBaseInput } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/base-input-styled/base.styled";
import { Link } from "@com.mgmtp.a12.widgets/widgets-core/lib/link";

import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { loadAssigneeCandidates } from "../../../app/slices/users/actions";
import { AssigneeCandidate } from "../../../app/slices/users/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { DocumentModelContext } from "../../../component/auxiliaryOverviewEngine/context";
import { assertNonNullable, assertTrue } from "../../../utilities/assert";
import { ModulFDataContext } from "../../processFormEngine/components/contexts";
import { isDocumentWithProcessMetadata } from "../../workflows/data/processMetadata";
import { isDocumentWithRoot } from "../../../utilities/loadAppmodelTypeguards";

import { selectAssigneeCandidatesCreator } from "../selectors";
import { selectAssigneeFromDocument } from "../utils";

import StyledFieldSuffixWrapper = StyledBaseInput.StyledFieldSuffixWrapper;
import StyledFieldTextInput = StyledBaseInput.StyledFieldTextInput;
/* Workaround to hide the clear button (X) from the assignee autocomplete
   Can be removed once A12 does A12-17163
 */
const NoClearButtonAutocomplete = styled(Autocomplete)`
	& ${StyledFieldSuffixWrapper}:has(> button) {
		display: none;
	}
	& ${StyledFieldTextInput}:not(:focus) {
		cursor: pointer !important;
	}
`;

const iconStyle = { color: "gray" };

type AssigneePickerProps = Omit<
	AutocompleteProps,
	| "closeAndResetOption"
	| "prefixes"
	| "hintTemplate"
	| "onValueChange"
	| "items"
	| "links"
	| "onSearch"
	| "loading"
	| "loadingLabel"
	| "inputPlaceHolder"
	| "value"
	| "initialValue"
	| "openOnFocus"
> & { readonly id: string };

const AssigneePicker: React.ComponentType<
	AssigneePickerProps & {
		readonly selected: AssigneeCandidate | undefined;
		readonly candidates: readonly AssigneeCandidate[] | undefined;
		readonly fqn: string | undefined;
		onChange(selected: AssigneeCandidate | undefined): void;
		onLoad?(): void;
	}
> = function AssigneePicker({ selected, candidates, fqn, onChange, onLoad, ...props }) {
	const user = useSelector(UaaSelectors.user);
	assertTrue(isUaaOidcUser(user));

	const currentUsername = user.profile.preferred_username;
	assertNonNullable(currentUsername);

	const value = useMemo<DropDownItem | undefined>(
		() =>
			selected
				? {
						label: `${selected.firstName} ${selected.lastName}`,
						value: selected.username
					}
				: undefined,
		[selected]
	);

	const [filter, setFilter] = useState<string>("");

	const items = useMemo<DropDownItem[]>(
		() =>
			(candidates ?? [])
				.filter(it => it.username !== currentUsername)
				.filter(it => !fqn || it.fqns?.includes(fqn))
				.map(it => ({ label: `${it.firstName} ${it.lastName}`, value: it.username }))
				.filter(
					it =>
						it.label.toLowerCase().startsWith(filter.toLowerCase()) ||
						it.value?.toLowerCase().startsWith(filter.toLowerCase())
				),
		[candidates, filter, currentUsername, fqn]
	);

	const closeMenu = useRef<() => void>(() => {});

	const links = useMemo(() => {
		return [
			selected?.username !== currentUsername && (
				<Link
					useAsButton
					onClick={() => {
						closeMenu.current();
						setFilter("");
						assertNonNullable(user.profile.preferred_username);
						onChange({
							username: user.profile.preferred_username,
							firstName: user.profile.given_name,
							lastName: user.profile.family_name,
							fullName:
								[user.profile.given_name, user.profile.family_name].filter(it => !!it).join(" ") ||
								undefined
						});
					}}
				>
					<Icon>person_add</Icon> Mir zuweisen
				</Link>
			),
			selected && (
				<Link
					useAsButton
					onClick={() => {
						closeMenu.current();
						setFilter("");
						onChange(undefined);
					}}
				>
					<Icon>person_remove</Icon> Zuweisung entfernen
				</Link>
			)
		].filter(it => !!it);
	}, [
		selected,
		currentUsername,
		user.profile.preferred_username,
		user.profile.given_name,
		user.profile.family_name,
		onChange,
		setFilter
	]);

	const onValueChange = useCallback<NonNullable<AutocompleteProps["onValueChange"]>>(
		newValue => {
			assertTrue(typeof newValue !== "string");
			setFilter("");
			if (selected !== newValue.value) {
				onChange(candidates?.find(it => it.username === newValue.value));
			}
		},
		[selected, candidates, onChange, setFilter]
	);

	const inputRef = useCallback<NonNullable<AutocompleteProps["inputRef"]>>(
		node => {
			node?.addEventListener("focus", () => node.select());
			props.inputRef?.(node);
		},
		[props.inputRef]
	);

	const closeAndResetOption = useCallback<NonNullable<AutocompleteProps["closeAndResetOption"]>>(
		handler => (closeMenu.current = () => setTimeout(handler, 0)),
		[]
	);

	const onSearch = useCallback<NonNullable<AutocompleteProps["onSearch"]>>(
		value => {
			if (!candidates?.length) {
				onLoad?.();
			}

			//  Must use flushSync because the Autocomplete widget does a property-level
			// compare before resetting the input display string
			flushSync(() => setFilter(" "));
			setFilter(value);
		},
		[candidates, onLoad, setFilter]
	);

	const inputProps = useMemo(() => ({ placeholder: "Nicht zugewiesen" }), []);

	return (
		<NoClearButtonAutocomplete
			{...props}
			className={joinClassNames("assignee-picker", props.className)}
			inputRef={inputRef}
			closeAndResetOption={closeAndResetOption}
			prefixes={<Icon style={iconStyle}>account_circle</Icon>}
			hintTemplate={(candidates?.length ?? 0) > 0 ? "{count} Nutzer:innen" : ""}
			onValueChange={onValueChange}
			items={items}
			value={value}
			links={links}
			onSearch={onSearch}
			loading={candidates === undefined}
			loadingLabel="Laden..."
			inputProps={inputProps}
			openOnFocus={false}
		/>
	);
};

export const Wiedervorlagenzuweisung: React.ComponentType<AutocompleteProps> = function Wiedervorlagenzuweisung(props) {
	const dispatch = useDispatch();

	const activityId = React.useContext(ActivityIdContext);
	assertNonNullable(activityId);
	const path = React.useContext(ModulFDataContext);
	const instancePath = React.useMemo(() => path.slice(0, path.length - 1), [path]);

	const fachverfahren = useSelector(
		ActivitySelectors.activityPropById(activityId, it => it.descriptor.fachverfahren)
	);
	const manifest = useSelector(getFVManifest(fachverfahren));
	const fvKey = manifest?.identification.key;
	assertNonNullable(fvKey);

	const engineState = useSelector(FormEngineSelectors.engineState(activityId));
	const document = engineState && DataSelectors.document()(engineState);

	const client = getClient(document);
	const fqn = client ? `${fvKey}_${client}` : undefined;

	const data = useSelector(FormEngineSelectors.dataState(activityId));
	assertNonNullable(FormActivity.Data.SingleDocumentData.isInstance(data));

	const candidates = useSelector(selectAssigneeCandidatesCreator(fvKey, "Wiedervorlagenzuweisung"));

	const onChange = useCallback<(assignee: AssigneeCandidate | undefined) => void>(
		selected => {
			PROPERTY_MAP.forEach(([key, elementName]) => {
				const value = selected?.[key] ?? null;
				const path = [...instancePath, { elementName, index: 1 }];
				dispatch(FormEngineActions.event({ activityId, engineEvent: Events.valueChange({ value, path }) }));
			});
		},
		[dispatch, activityId, instancePath]
	);

	assertNonNullable(props.id);

	return (
		<DocumentAssigneePicker
			{...props}
			id={props.id}
			fvKey={fvKey}
			fqn={fqn}
			document={data.document}
			instancePath={instancePath}
			candidates={candidates}
			onChange={onChange}
		/>
	);
};

const PROPERTY_MAP = [
	["username", "id"] as const,
	["firstName", "firstName"] as const,
	["lastName", "lastName"] as const,
	["fullName", "fullName"] as const
];

export const OverviewAssigneePicker: ComponentType<
	Omit<AssigneePickerProps, "label" | "hideLabel"> & {
		readonly activityId: string;
		readonly fvKey: string;
		readonly fqn: string | undefined;
		readonly elementRef: string;
		readonly document: JSONDocument;
		readonly localizedModelText: LocalizedModelText | undefined;
		readonly candidates: readonly AssigneeCandidate[] | undefined;
		onChange(selected: AssigneeCandidate | undefined): void;
	}
> = function OverviewAssigneePicker({ elementRef, localizedModelText, ...props }) {
	const documentModelContextValue = useContext(DocumentModelContext);
	assertNonNullable(documentModelContextValue);
	const { documentModelSearchService } = documentModelContextValue;

	const path = documentModelSearchService.getPathById(elementRef);
	assertNonNullable(path);

	const overviewElementLocalizer = LocalizerHooks.useLocalizedOverviewElement();
	const documentElementLocalizer = LocalizerHooks.useLocalizedFieldLabel();

	const label = overviewElementLocalizer(["none"], localizedModelText) || documentElementLocalizer(path);

	const instancePath = path.slice(0, path.length - 1).map(it => ({ ...it, index: 1 }));
	return <DocumentAssigneePicker {...props} label={label} hideLabel instancePath={instancePath} />;
};

export const DocumentAssigneePicker: ComponentType<
	AssigneePickerProps & {
		readonly fvKey: string;
		readonly fqn: string | undefined;
		readonly instancePath: EntityInstancePath;
		readonly document: JSONDocument;
		readonly candidates: readonly AssigneeCandidate[] | undefined;
		onChange(selected: AssigneeCandidate | undefined): void;
	}
> = function FvAssigneePicker({ fvKey, fqn, instancePath, document, ...props }) {
	const dispatch = useDispatch();
	const selected = selectAssigneeFromDocument(document, instancePath);
	const onLoad = useCallback(() => dispatch(loadAssigneeCandidates({ fvKey })), [dispatch, fvKey]);
	return <AssigneePicker {...props} selected={selected} fqn={fqn} onLoad={onLoad} />;
};

const getClient = (document: object | undefined) => {
	if (isDocumentWithRoot(document)) {
		if (isDocumentWithCorrelatedFachverfahrenClient(document)) {
			return document.root.correlatedFachverfahrenClient;
		} else if (isDocumentWithProcessMetadata(document)) {
			return document.root.processMetadata.fachverfahren.client;
		}
	}
	return undefined;
};

export const isDocumentWithCorrelatedFachverfahrenClient = new IsInterface()
	.withProperties({
		root: new IsInterface()
			.withProperties({
				correlatedFachverfahrenClient: isString
			})
			.get()
	})
	.get();
export type DocumentWithCorrelatedFachverfahrenClient = GuardedType<typeof isDocumentWithCorrelatedFachverfahrenClient>;
